import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  getTemplates,
  getArchivedTemplates,
  getUnsubscribeTemplates,
  getGlobalUnsubscribeTemplates,
  getGlobalEmailTrackingUnsubscribeTemplates,
  getEmailTrackingUnsubscribeSiteTemplates,
  getSendMyBasketOnsiteTemplates,
  getEmailAcquisitions,
  getMPSEmbedTemplates,
  getMPSPopupTemplates,
  getDisplayOnlyTemplates,
} from '../utils/queries';
import { TAB_TO_TEMPLATE_TYPE } from '../constants/tabs';
import { AccessLevel } from 'entities/user';
import { ITemplate } from 'entities/template/template';

interface UseMainTemplatesQueryOptions {
  isArchived?: boolean;
  channel?: string;
  templateType?: string;
}

export interface TransformedTemplatesData {
  emailCartAbandonment: ITemplate[];
  emailBrowseAbandonment: ITemplate[];
  smsCartAbandonment: ITemplate[];
  smsBrowseAbandonment: ITemplate[];
  rcsCartAbandonment: ITemplate[];
  rcsBrowseAbandonment: ITemplate[];
  sendMyBasketEmail: ITemplate[];
  emailAcquisitionEmail: ITemplate[];
  bulkRetargeting: ITemplate[];
}

const transformTemplatesData = (data: ITemplate[]): TransformedTemplatesData => ({
  emailCartAbandonment: data.filter(t => t.channel === 'email' && t.type === 'cartAbandonment'),
  emailBrowseAbandonment: data.filter(t => t.channel === 'email' && t.type === 'browseAbandonment'),
  smsCartAbandonment: data.filter(t => t.channel === 'sms' && t.type === 'cartAbandonment'),
  smsBrowseAbandonment: data.filter(t => t.channel === 'sms' && t.type === 'browseAbandonment'),
  rcsCartAbandonment: data.filter(t => t.channel === 'rcs' && t.type === 'cartAbandonment'),
  rcsBrowseAbandonment: data.filter(t => t.channel === 'rcs' && t.type === 'browseAbandonment'),
  sendMyBasketEmail: data.filter(t => t.type === 'sendMyBasket' && t.channel === 'email'),
  emailAcquisitionEmail: data.filter(t => t.type === 'emailAcquisition' && t.channel === 'email'),
  bulkRetargeting: data.filter(t => t.channel === 'email' && t.type === 'bulkRetargeting'),
});

export const useMainTemplatesQuery = (siteId: string, options: UseMainTemplatesQueryOptions = {}) => {
  const { t } = useTranslation('designs');
  const { isArchived, channel, templateType } = options;
  const backendTemplateType = templateType ? TAB_TO_TEMPLATE_TYPE[templateType] || templateType : undefined;

  return useQuery(
    ['templates', isArchived, channel, backendTemplateType, siteId],
    () =>
      isArchived && channel ? getArchivedTemplates({ siteId, channel, templateType, t }) : getTemplates({ siteId, t }),
    {
      enabled: !isArchived || !!channel,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      select: isArchived ? undefined : transformTemplatesData,
    },
  );
};

export const useUnsubscribeTemplatesQuery = (siteId: string) => {
  const { t } = useTranslation('designs');
  return useQuery(['unsubscribes', siteId], () => getUnsubscribeTemplates({ siteId, t }));
};

export const useGlobalUnsubscribeTemplatesQuery = (siteId: string, accessLevel: AccessLevel) => {
  const { t } = useTranslation('designs');
  return useQuery(['globalUnsubscribes', siteId, accessLevel], () =>
    getGlobalUnsubscribeTemplates({ siteId, t, accessLevel }),
  );
};

export const useGlobalEmailTrackingUnsubscribeTemplatesQuery = (
  siteId: string,
  accessLevel: AccessLevel,
  options?: { enabled?: boolean },
) => {
  const { t } = useTranslation('designs');
  return useQuery(
    ['globalEmailTrackingUnsubscribes', siteId, accessLevel],
    () => getGlobalEmailTrackingUnsubscribeTemplates({ siteId, t, accessLevel }),
    { enabled: options?.enabled ?? true },
  );
};

export const useEmailTrackingUnsubscribeSiteTemplatesQuery = (siteId: string, options?: { enabled?: boolean }) => {
  const { t } = useTranslation('designs');
  return useQuery(
    ['emailTrackingUnsubscribes', siteId],
    () => getEmailTrackingUnsubscribeSiteTemplates({ siteId, t }),
    { enabled: options?.enabled ?? true },
  );
};

export const useEmailAcquisitionsQuery = (siteId: string) => {
  const { t } = useTranslation('designs');
  return useQuery(['emailAcquisitions', siteId], () => getEmailAcquisitions({ siteId, t }));
};

export const useSendMyBasketQuery = (siteId: string) => {
  const { t } = useTranslation('designs');
  return useQuery(['sendMyBasket', siteId], () => getSendMyBasketOnsiteTemplates({ siteId, t }));
};

export const useDisplayOnlyQuery = (siteId: string) => {
  const { t } = useTranslation('designs');
  return useQuery(['displayOnly', siteId], () => getDisplayOnlyTemplates({ siteId, t }));
};

export const useMPSEmbedQuery = (siteId: string) => {
  const { t } = useTranslation('designs');
  return useQuery(['mpsEmbed', siteId], () => getMPSEmbedTemplates({ siteId, t }));
};

export const useMPSPopupQuery = (siteId: string) => {
  const { t } = useTranslation('designs');
  return useQuery(['mpsPopup', siteId], () => getMPSPopupTemplates({ siteId, t }));
};
